import type { DataTableView, FilterDef } from './types';

/** The filter a table's pill offers unless it names another. */
const DEFAULT_PILL_FILTER = 'status';

/** Which filter a table's pill offers: the one named, else `status` when there is one. */
export function pillFilterDef<TData>(
  defs: ReadonlyArray<FilterDef<TData>>,
  named: string | false | undefined,
  hasOwnViews: boolean,
): FilterDef<TData> | undefined {
  if (named === false) return undefined;
  // A table with its own views and no named filter has chosen what its pill holds.
  if (named === undefined && hasOwnViews) return undefined;
  const def = defs.find((candidate) => candidate.key === (named ?? DEFAULT_PILL_FILTER));
  return def?.type === 'enum' || def?.type === 'boolean' ? def : undefined;
}

/**
 * "All" plus one view per value of an enum or yes/no filter. Picking one changes only that filter,
 * so a search or another filter on screen stays.
 */
export function pillViews<TData>(
  def: FilterDef<TData>,
  labels: { all: string; yes: string; no: string },
): DataTableView[] {
  const pillFilter = def.key;
  const all: DataTableView = { id: 'all', label: labels.all, pillFilter, query: { filters: {} } };
  if (def.type === 'enum') {
    return [
      all,
      ...def.options.map((option): DataTableView => ({
        id: `${def.key}:${option.value}`,
        label: option.label,
        pillFilter,
        query: { filters: { [def.key]: { op: 'in', values: [option.value] } } },
      })),
    ];
  }
  if (def.type === 'boolean') {
    return [
      all,
      ...[true, false].map((value): DataTableView => ({
        id: `${def.key}:${String(value)}`,
        label: value ? (def.trueLabel ?? labels.yes) : (def.falseLabel ?? labels.no),
        pillFilter,
        query: { filters: { [def.key]: { op: 'eq', value } } },
      })),
    ];
  }
  return [];
}
